/**
 * SlatWall accessories — where a hook, basket or shelf may go on a section.
 *
 * An accessory stays on the wall by two tabs bent back from its plate, which
 * reach into two of the panel's grooves (data/slatwallAccessories.ts: `seat`),
 * and it hangs with the bottom of its lower tab resting on the bottom edge of
 * that groove's mouth. So it can only ever sit at those heights: the lower
 * tab in one groove, the upper in the next one up. Each 6" row of SlatWall
 * has two grooves (room3d/profiles/slatwall: the mouths open 2.54" and 5.51"
 * up the profile, and the builder drops every panel by its ½" lip), so a
 * section of n rows has 2n grooves 3" apart, and an accessory has 2n − 1 seats.
 *
 * At a seat the accessory's body (its full height) may reach above the
 * section's top or below its bottom — a basket hangs below the slats onto
 * the boards — but never off the WALL; along the wall it stays on its
 * section, since the grooves are the section's. It overlaps no other
 * accessory on the surface, and no window or door.
 *
 * Positions are kept relative to the section (types/sections.ts); the helpers
 * here turn them into surface rectangles, snap and check a move, and find a
 * free seat for a new one. Pure, like the rest of calculations/sections.
 */

import { Opening, Section, SlatWallAccessory, SlatWallAccessoryKind, Surface } from '../../types/sections';
import { accessoryDef } from '../../data/slatwallAccessories';
import { SLATWALL_COVERAGE_HEIGHT } from '../constants';
import { Rect, intersectRect } from './resolver';

/** 3" — the groove pitch up a SlatWall panel (and the step a nudge takes). */
export const SLAT_SLOT_FT = 0.25;

/**
 * The bottom edge of each groove's mouth in a row, feet above the row's
 * bottom: from the traced profile (room3d/profiles/slatwallProfilePoints:
 * the mouths open at 2.538" and 5.511"), less the ½" lip the builder drops
 * every panel by. An accessory hangs with its lower tab's bottom resting here.
 */
const GROOVES_PER_ROW = [2.0377 / 12, 5.0107 / 12];

const EPS = 1e-6;

/** The accessory's rectangle on the SURFACE (feet). */
export function accessoryRect(section: Section, acc: { kind: SlatWallAccessoryKind; x: number; y: number }): Rect {
  const { w, h } = accessoryDef(acc.kind).size;
  return { x: section.x + acc.x, y: section.y + acc.y, width: w, height: h };
}

/** Every accessory on the surface with its section and surface rectangle. */
export function surfaceAccessories(surface: Surface): { section: Section; accessory: SlatWallAccessory; rect: Rect }[] {
  return surface.sections.flatMap((section) => (section.accessories ?? []).map((accessory) => ({ section, accessory, rect: accessoryRect(section, accessory) })));
}

export function findAccessory(surface: Surface, id: string): { section: Section; accessory: SlatWallAccessory } | null {
  for (const section of surface.sections) {
    const accessory = section.accessories?.find((a) => a.id === id);
    if (accessory) return { section, accessory };
  }
  return null;
}

/** The bottom edge of each groove mouth on the section, feet above its bottom, lowest first. */
export function grooveCentres(section: Section): number[] {
  const rows = Math.max(1, Math.round(section.height / SLATWALL_COVERAGE_HEIGHT));
  const out: number[] = [];
  for (let row = 0; row < rows; row++) for (const g of GROOVES_PER_ROW) out.push(row * SLATWALL_COVERAGE_HEIGHT + g);
  return out;
}

/**
 * Where a kind can sit on the section: the section-relative y of its bottom
 * for each groove its lower tab can rest in (its upper tab in the next groove
 * up, so not the topmost), highest first. It hangs with the bottom of the
 * lower tab on the bottom edge of the groove's mouth.
 */
export function seatPositions(section: Section, kind: SlatWallAccessoryKind): number[] {
  const { seat } = accessoryDef(kind);
  const grooves = grooveCentres(section);
  const out: number[] = [];
  for (let i = grooves.length - 2; i >= 0; i--) out.push(grooves[i] - seat.tabBottom);
  return out;
}

/** The seat nearest a section-relative y (the accessory's bottom). */
export function snapToGroove(section: Section, kind: SlatWallAccessoryKind, y: number): number {
  const seats = seatPositions(section, kind);
  if (seats.length === 0) return 0;
  let best = seats[0];
  for (const s of seats) if (Math.abs(s - y) < Math.abs(best - y)) best = s;
  return best;
}

export type AccessoryIssue = 'off-section' | 'overlap' | 'opening' | 'not-slatwall';

/**
 * May an accessory sit at (x, y) (section-relative) on this section? Checks
 * the section (along the wall), the wall (up it), the surface's other
 * accessories (leaving out `ignoreId`) and the surface's openings. The seat
 * is not checked here: callers snap first.
 */
export function checkAccessory(
  surface: Surface, section: Section, kind: SlatWallAccessoryKind, x: number, y: number, ignoreId?: string,
): AccessoryIssue | null {
  if (section.productType !== 'SlatWall') return 'not-slatwall';
  const { w, h } = accessoryDef(kind).size;
  if (x < -EPS || x + w > section.width + EPS) return 'off-section';
  const rect: Rect = { x: section.x + x, y: section.y + y, width: w, height: h };
  if (rect.y < -EPS || rect.y + h > surface.height + EPS) return 'off-section';
  for (const other of surfaceAccessories(surface)) {
    if (other.accessory.id === ignoreId) continue;
    if (intersectRect(rect, other.rect)) return 'overlap';
  }
  for (const o of surface.openings ?? []) {
    if (intersectRect(rect, openingRect(o))) return 'opening';
  }
  return null;
}

const openingRect = (o: Opening): Rect => ({ x: o.x, y: o.y, width: o.width, height: o.height });

/**
 * A move of an accessory by (dx, dy) feet, resolved: kept on its section along
 * the wall, its flanges seated in the nearest grooves. Returns the new
 * position, or null if that spot is taken (the caller leaves it where it was).
 */
export function resolveAccessoryMove(surface: Surface, section: Section, acc: SlatWallAccessory, dx: number, dy: number): { x: number; y: number } | null {
  const { w } = accessoryDef(acc.kind).size;
  const x = Math.max(0, Math.min(section.width - w, acc.x + dx));
  const y = snapToGroove(section, acc.kind, acc.y + dy);
  if (checkAccessory(surface, section, acc.kind, x, y, acc.id)) return null;
  return { x, y };
}

/**
 * A free seat for a new accessory of `kind` on the section: the highest seat
 * first, from the middle outward in 3" steps. Null when nothing fits.
 */
export function freeAccessorySpot(surface: Surface, section: Section, kind: SlatWallAccessoryKind): { x: number; y: number } | null {
  const { w } = accessoryDef(kind).size;
  const step = SLAT_SLOT_FT;
  for (const y of seatPositions(section, kind)) {
    // Centre-out across the section: a lone hook lands in the middle, the next beside it.
    const xs: number[] = [];
    const mid = (section.width - w) / 2;
    for (let d = 0; mid - d >= -EPS; d += step) {
      xs.push(mid - d);
      if (d > 0) xs.push(mid + d);
    }
    for (const x0 of xs) {
      const x = Math.round(Math.max(0, Math.min(section.width - w, x0)) / step) * step;
      if (!checkAccessory(surface, section, kind, x, y)) return { x, y };
    }
  }
  return null;
}

/** The accessories on these surfaces, counted by kind (the designer's readout; the bill, once they are products). */
export function countAccessories(surfaces: Surface[]): { kind: SlatWallAccessoryKind; sku: string; label: string; quantity: number }[] {
  const out = new Map<SlatWallAccessoryKind, { kind: SlatWallAccessoryKind; sku: string; label: string; quantity: number }>();
  for (const surface of surfaces) {
    for (const section of surface.sections) {
      for (const acc of section.accessories ?? []) {
        const def = accessoryDef(acc.kind);
        const cur = out.get(acc.kind) ?? { kind: acc.kind, sku: def.sku, label: def.label, quantity: 0 };
        cur.quantity += 1;
        out.set(acc.kind, cur);
      }
    }
  }
  return [...out.values()].sort((a, b) => a.sku.localeCompare(b.sku));
}

/** An accessory id not yet used on the surface. */
export function nextAccessoryId(surface: Surface, section: Section): string {
  const used = new Set(surfaceAccessories(surface).map((a) => a.accessory.id));
  let n = 1;
  while (used.has(`${section.id}-acc-${n}`)) n++;
  return `${section.id}-acc-${n}`;
}

/** The section's accessories that still fit it (after a resize, say), re-seated; the rest are dropped. */
export function fittingAccessories(surface: Surface, section: Section): SlatWallAccessory[] | undefined {
  if (!section.accessories?.length) return undefined;
  if (section.productType !== 'SlatWall') return undefined;
  const kept: SlatWallAccessory[] = [];
  const trial: Section = { ...section, accessories: [] };
  const others = surface.sections.filter((s) => s.id !== section.id);
  for (const acc of section.accessories) {
    const y = snapToGroove(section, acc.kind, acc.y);
    const probe: Surface = { ...surface, sections: [...others, { ...trial, accessories: kept }] };
    if (!checkAccessory(probe, trial, acc.kind, acc.x, y)) kept.push({ ...acc, y });
  }
  return kept.length ? kept : undefined;
}
